import React from "react";
import {
  Document,
  Image,
  Page,
  PDFViewer,
  Text,
  View,
} from "@react-pdf/renderer";
import dayjs from "dayjs";
import { Request } from "../../hooks/Request";
import {
  formatearMontoBolivianos,
  numberWithDecimals,
  validateArray,
} from "../../utils/validate";

// Constantes
const FIELD_LIST = [
  "numeroDeFactura",
  "codigoDeInterno",
  "fechaDePlanilla",
  "modalidadDeDespacho",
  "descripciónDeLaMercancía",
  "partidaArancelaria",
  "a1NúmeroDeDeclaración",
  "a3NúmeroDeReferencia",
  "a2FechaDeAceptación",
  "a4AduanaDeDespacho",
  "tributosAduaneros",
  "gastosNoFacturados",
  "gastosFacturados",
  "honorariosProfesionales",
  "pagos",
  "g1TributosDeterminadosGa",
  "g2TributosDeterminadosIva",
  "g4TributosDeterminadosIce",
];

const DEFAULT_TRIBUTOS = [
  { name: "Gravamen Arancelario (G.A.)", key: "ga" },
  { name: "Impto. Valor Agregado (I.V.A.)", key: "iva" },
  { name: "Impto. Consumo Especifico(I.C.E.)", key: "ice" },
  { name: "Form. SISTEMA - ANB", value: 100 },
];

// Estilos PDF
const styles = {
  page: {
    padding: "40px 65px 0 60px",
    fontFamily: "Helvetica",
    fontSize: 11,
  },
  cell: {
    paddingVertical: 2,
    paddingHorizontal: 3,
  },
  bold: {
    fontFamily: "Helvetica-Bold",
  },
  row: {
    display: "flex",
    flexDirection: "row",
    gap: 4,
  },
  col: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  border: {
    borderWidth: 1,
  },
  line: {
    flex: 1,
    borderBottomWidth: 2.4,
    borderBottomColor: "black",
    marginHorizontal: 5,
  },
  formatBackground: {
    position: "absolute",
    top: "0",
    left: "0",
    zIndex: -10,
    width: "612px",
    height: "792px",
  },
  // Utilidades de ancho
  w10: { width: "10%" },
  w18: { width: "18%" },
  w20: { width: "20%" },
  w28: { width: "28%" },
  w40: { width: "40%" },
  w60: { width: "60%" },
  w90: { width: "90%" },
  w100: { width: "100%" },
};

const PaymentSection = ({ sections }) => {
  const total = sections.items.reduce(
    (acc, section) =>
      acc +
      section.items.reduce((sum, item) => sum + Number(item.value || 0), 0),
    0,
  );

  return (
    <View
      style={[
        styles.col,
        {
          gap: 2,
          paddingHorizontal: 8,
          paddingTop: 8,
          paddingBottom: 6,
          borderRadius: "5px",
          borderWidth: 2.4,
        },
      ]}
    >
      {/* Título de la sección */}
      <Text
        style={[
          styles.cell,
          styles.bold,
          {
            position: "absolute",
            backgroundColor: "white",
            transform: "translate(8px -13px)",
          },
        ]}
      >
        {sections.title}
      </Text>

      {/* Renderizar items */}
      {sections.items.map((section, index) => (
        <View key={index}>
          {section.title && (
            <View style={styles.row}>
              <Text style={styles.w90}>
                -----------{section.title}-----------
              </Text>
            </View>
          )}

          {section.items.map((item, idx) => (
            <View key={idx} style={styles.row}>
              <Text style={styles.w90}>{item.name}</Text>
              <Text style={[styles.w10, { textAlign: "right" }]}>
                {numberWithDecimals(item.value)}
              </Text>
            </View>
          ))}

          {index < sections.items.length - 1 && section.title && (
            <>
              <Text style={styles.separator}>
                ***************************************
              </Text>
              <Text style={{ height: 6 }} />
            </>
          )}
        </View>
      ))}

      {/* Subtotal */}
      <View
        style={[
          styles.row,
          styles.bold,
          {
            minWidth: "6cm",
            backgroundColor: "white",
            position: "absolute",
            width: "4cm",
            right: 0,
            bottom: 0,
            transform: "translate(-4px 10px)",
          },
        ]}
      >
        <Text style={styles.cell}>SubTotal:</Text>
        <View
          style={[
            styles.border,
            styles.row,
            { width: "4cm", textAlign: "right", paddingHorizontal: 2 },
          ]}
        >
          <Text style={styles.w40}>Bs.</Text>
          <Text style={styles.w60}>{numberWithDecimals(total)}</Text>
        </View>
      </View>
    </View>
  );
};

const useTributosCalculation = (data) => {
  const extractNumber = (str) => {
    if (!str) return 0;
    const parts = str.split(" ");
    return Number(parts[parts.length - 1] || "0");
  };

  const ga = extractNumber(data?.g1TributosDeterminadosGa);
  const iva = extractNumber(data?.g2TributosDeterminadosIva);
  const ice = extractNumber(data?.g4TributosDeterminadosIce);

  const getTributosAduaneros = (customList) => {
    const defaultWithValues = DEFAULT_TRIBUTOS.map((tributo) => ({
      ...tributo,
      value:
        tributo.key === "ga"
          ? ga
          : tributo.key === "iva"
            ? iva
            : tributo.key === "ice"
              ? ice
              : tributo.value || 0,
    }));

    if (!customList || !validateArray(customList)) {
      return defaultWithValues;
    }

    const existingItems = customList.map((item) => ({
      name: item.label,
      value: Number(item.description || 0),
    }));

    const existingNames = new Set(existingItems.map((item) => item.name));
    const combinedItems = [...existingItems];

    defaultWithValues.forEach((tributo) => {
      if (!existingNames.has(tributo.name)) {
        combinedItems.push({
          name: tributo.name,
          value: tributo.value,
        });
      }
    });

    return combinedItems.reverse();
  };

  return { getTributosAduaneros };
};

const PDFDocument = ({ data }) => {
  const { getTributosAduaneros } = useTributosCalculation(data);

  const numeros = require("numeros_a_palabras");
  numeros.numero2word().Config._setSingular("BOLIVIANO");
  numeros.numero2word().Config._setPlural("BOLIVIANOS");
  numeros.numero2word().Config._setCentsSingular("CENTAVO");
  numeros.numero2word().Config._setCentsPlural("CENTAVOS");

  const tributosAduaneros =
    getTributosAduaneros(data?.tributosAduanerosSubDatas)?.reduce(
      (acc, item) => acc + Number(item.value || 0),
      0,
    ) || 0;

  const gastosNoFacturados =
    data?.gastosNoFacturadosSubDatas?.reduce(
      (acc, item) => acc + Number(item.description || 0),
      0,
    ) || 0;

  const gastosFacturados =
    data?.gastosFacturadosSubDatas?.reduce(
      (acc, item) => acc + Number(item.description || 0),
      0,
    ) || 0;

  const honorariosProfesionales =
    data?.honorariosProfesionalesSubDatas?.reduce(
      (acc, item) => acc + Number(item.description || 0),
      0,
    ) || 0;

  const pagos =
    data?.pagosSubDatas?.reduce(
      (acc, item) => acc + Number(item.description || 0),
      0,
    ) || 0;

  const totalGastos =
    tributosAduaneros +
    gastosNoFacturados +
    gastosFacturados +
    honorariosProfesionales;
  const saldoFinal = totalGastos - pagos;

  const formatDate = (date) => {
    return dayjs(date || new Date()).format("D [de] MMMM [de] YYYY");
  };

  return (
    <Document>
      <Page size="LETTER" style={styles.page}>
        <View style={styles.formatBackground}>
          <Image
            src="https://res.cloudinary.com/joeldes/image/upload/v1751556894/DeliveryDay/reports/DD_formato-informe_xdmzwo.jpg"
            style={{ width: "100%", height: "100%" }}
          />
        </View>

        <View style={[styles.col, styles.bold, { gap: 0 }]}>
          <Text style={{ textAlign: "right" }}>
            {data?.numeroDeFactura || ""}
          </Text>
          <Text style={{ textAlign: "center" }}>PLANILLA DE GASTOS</Text>
        </View>

        <View style={[styles.col, { fontSize: 9 }]}>
          <Text style={{ paddingVertical: 4 }}>
            La Paz, {formatDate(data?.fechaDePlanilla)}
          </Text>
          <Text style={styles.bold}>
            Señores: {data?.company?.razonSocial || ""}
          </Text>
          <Text style={styles.bold}>
            Mercadería: {data?.descripciónDeLaMercancía || ""}
          </Text>
        </View>

        <View
          style={[
            styles.row,
            styles.w100,
            { gap: 6, fontSize: 9, paddingTop: "1cm" },
          ]}
        >
          <View style={[styles.col, styles.w18, { textAlign: "center" }]}>
            <Text style={styles.bold}>Referencia</Text>
            <Text style={[styles.border, styles.cell]}>
              {data?.a3NúmeroDeReferencia || ""}
            </Text>
          </View>
          <View style={[styles.col, styles.w28, { textAlign: "center" }]}>
            <Text style={styles.bold}>DIM/DEX</Text>
            <Text style={[styles.border, styles.cell]}>
              {data?.a1NúmeroDeDeclaración || "-"}
            </Text>
          </View>
          <View style={[styles.col, styles.w18, { textAlign: "center" }]}>
            <Text style={styles.bold}>Fecha Registro</Text>
            <Text style={[styles.border, styles.cell]}>
              {data?.a2FechaDeAceptación?.split(" ")[0] || ""}
            </Text>
          </View>
          <View style={[styles.col, styles.w28, { textAlign: "center" }]}>
            <Text style={styles.bold}>Modalidad de despacho</Text>
            <Text style={[styles.border, styles.cell]}>
              {data?.modalidadDeDespacho || ""}
            </Text>
          </View>
          <View style={[styles.col, styles.w10, { textAlign: "center" }]}>
            <Text style={styles.bold}>Aduana</Text>
            <Text style={[styles.border, styles.cell]}>
              {data?.a4AduanaDeDespacho?.split("-")[0] || "-"}
            </Text>
          </View>
        </View>

        <View style={[styles.col, { gap: 15 }]}>
          <View
            style={[
              styles.row,
              styles.bold,
              {
                position: "relative",
                justifyContent: "center",
                alignItems: "center",
              },
            ]}
          >
            <Text style={[styles.w20, styles.line]}> </Text>
            <Text style={{ transform: "translateY(5px)", letterSpacing: 2 }}>
              DETALLE
            </Text>
            <Text style={[styles.w20, styles.line]}> </Text>
          </View>

          <PaymentSection
            sections={{
              title: "Tributos Aduaneros",
              items: [
                {
                  items: getTributosAduaneros(
                    data?.tributosAduanerosSubDatas,
                  ).map((item) => ({
                    name: item.name,
                    value: Number(item.value || 0),
                  })),
                },
              ],
            }}
          />

          <PaymentSection
            sections={{
              title: "Gastos de Despacho Aduanero",
              items: [
                {
                  title: "GASTOS NO FACTURADOS",
                  items:
                    data?.gastosNoFacturadosSubDatas &&
                    validateArray(data.gastosNoFacturadosSubDatas)
                      ? data.gastosNoFacturadosSubDatas.map((item) => ({
                          name: item.label,
                          value: Number(item.description || 0),
                        }))
                      : [],
                },
                {
                  title: "GASTOS FACTURADOS",
                  items:
                    data?.gastosFacturadosSubDatas &&
                    validateArray(data.gastosFacturadosSubDatas)
                      ? data.gastosFacturadosSubDatas.map((item) => ({
                          name: item.label,
                          value: Number(item.description || 0),
                        }))
                      : [],
                },
              ],
            }}
          />

          <View style={{ paddingTop: 10 }}>
            <PaymentSection
              sections={{
                title: "Honorarios profesionales",
                items: [
                  {
                    items:
                      data?.honorariosProfesionalesSubDatas &&
                      validateArray(data.honorariosProfesionalesSubDatas)
                        ? data.honorariosProfesionalesSubDatas.map((item) => ({
                            name: item.label,
                            value: Number(item.description || 0),
                          }))
                        : [],
                  },
                ],
              }}
            />
          </View>

          <View
            style={[
              styles.col,
              {
                marginTop: 15,
                gap: 2,
                paddingHorizontal: 4,
                paddingVertical: 10,
                borderRadius: "5px",
                borderWidth: 2.4,
              },
            ]}
          >
            <View
              style={[
                styles.row,
                styles.bold,
                {
                  minWidth: "6cm",
                  backgroundColor: "white",
                  position: "absolute",
                  right: 0,
                  top: 0,
                  transform: "translate(-4px -10px)",
                },
              ]}
            >
              <Text style={styles.cell}>
                Total Gastos de Despacho Aduanero:
              </Text>
              <View
                style={[
                  styles.border,
                  styles.row,
                  {
                    width: "4cm",
                    textAlign: "right",
                    paddingHorizontal: 2,
                  },
                ]}
              >
                <Text style={styles.w40}>Bs.</Text>
                <Text style={styles.w60}>
                  {numberWithDecimals(totalGastos)}
                </Text>
              </View>
            </View>

            <View style={[styles.row, { textAlign: "right" }]}>
              <Text style={[styles.cell, { flex: 1 }]}>A cuenta:</Text>
              <View
                style={[
                  styles.border,
                  styles.row,
                  {
                    width: "4cm",
                    textAlign: "right",
                    paddingHorizontal: 2,
                  },
                ]}
              >
                <Text style={[styles.w40, { paddingVertical: 1 }]}>Bs.</Text>
                <Text style={[styles.w60, { paddingVertical: 1 }]}>
                  {numberWithDecimals(pagos)}
                </Text>
              </View>
            </View>

            <View
              style={[
                styles.row,
                styles.bold,
                {
                  minWidth: "6cm",
                  backgroundColor: "white",
                  position: "absolute",
                  right: 0,
                  bottom: 0,
                  transform: "translate(-4px 10px)",
                },
              ]}
            >
              <Text style={styles.cell}>
                Saldo Contable sujeto a Cancelación:
              </Text>
              <View
                style={[
                  styles.border,
                  styles.row,
                  {
                    width: "4cm",
                    textAlign: "right",
                    paddingHorizontal: 2,
                  },
                ]}
              >
                <Text style={styles.w40}>Bs.</Text>
                <Text style={styles.w60}>{numberWithDecimals(saldoFinal)}</Text>
              </View>
            </View>
          </View>

          <Text style={{ fontSize: 10 }}>
            <Text style={styles.bold}>Son: </Text>
            {formatearMontoBolivianos(Number(saldoFinal))}
            {/* {numeros.numero2word(saldoFinal.toFixed(2)).toString()} */}
          </Text>

          <View
            style={[
              styles.row,
              styles.bold,
              {
                position: "relative",
                justifyContent: "center",
                alignItems: "center",
              },
            ]}
          >
            <Text style={[styles.w20, styles.line]}> </Text>
            <Text style={{ transform: "translateY(5px)", letterSpacing: 2 }}>
              OBSERVACIONES
            </Text>
            <Text style={[styles.w20, styles.line]}> </Text>
          </View>

          <Text style={styles.cell}>
            Saldo contable sujeto a cancelación, depositar a la cuenta del banco
            Unión No.10000017356309
          </Text>
        </View>

        <View
          style={{
            position: "absolute",
            left: "-201px",
            bottom: "50%",
            width: 500,
            transform: "rotate(-90deg) translate(50%, 0)",
          }}
        >
          <Text
            style={[
              styles.bold,
              {
                fontSize: 16,
                textAlign: "center",
                width: "100%",
              },
            ]}
          >
            {data?.a1NúmeroDeDeclaración || "-"}
          </Text>
        </View>
      </Page>
    </Document>
  );
};

export const GenerateExpenseSheetPDF = ({ procedure }) => {
  const { data, loading } = Request({
    urlApi: `/Procedure/${
      procedure?.id || procedure?.procedureId
    }/dataValues?${FIELD_LIST.map((field) => `fieldnames=${field}`).join("&")}`,
  });

  dayjs.locale("es");

  if (!loading || !data) {
    return null;
  }

  return (
    <div>
      <PDFViewer
        style={{
          width: 900,
          height: window.innerHeight - 100,
        }}
      >
        <PDFDocument data={data} />
      </PDFViewer>
    </div>
  );
};
